import React, { useState, useEffect } from 'react';
import {
  X,
  Loader2,
  Receipt,
  Banknote,
  Smartphone,
  IndianRupee,
} from 'lucide-react';
import { useShift } from '../../../context/ShiftContext';
import shiftService from '../../../services/shiftService';
import toast from 'react-hot-toast';
import DenominationPanel, {
  getDenominationTotal,
  serializeDenomination,
} from './DenominationPanel';

export default function EndShiftModal({ onClose }) {
  const { activeShift, endShift, denominationEnabled } = useShift();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [closing, setClosing] = useState(false);
  const [denomCounts, setDenomCounts] = useState({});

  useEffect(() => {
    if (!activeShift?.id) return;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await shiftService.getShiftSummary(activeShift.id);
        setSummary(res?.data || res);
      } catch {
        toast.error('Failed to load shift summary');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [activeShift?.id]);

  // Sync denomination total to actualCash when denomination is enabled
  useEffect(() => {
    if (denominationEnabled) {
      const total = getDenominationTotal(denomCounts);
      setActualCash(total > 0 ? String(total) : '');
    }
  }, [denomCounts, denominationEnabled]);

  const handleClose = async () => {
    setClosing(true);
    try {
      const cashVal = actualCash !== '' ? parseFloat(actualCash) : null;
      const denomJson = denominationEnabled ? serializeDenomination(denomCounts) : null;
      await endShift(cashVal, notes || null, denomJson);
      toast.success('Shift closed successfully!');
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to close shift';
      toast.error(msg);
    } finally {
      setClosing(false);
    }
  };

  const getDuration = () => {
    if (!activeShift?.startedAt) return '—';
    const start = new Date(activeShift.startedAt);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const expectedCash =
    summary
      ? (Number(summary.openingCash) || 0) + (Number(summary.cashSales) || 0)
      : 0;
  const cashDiff =
    actualCash !== '' ? parseFloat(actualCash) - expectedCash : null;

  const fmt = (val) =>
    Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden flex flex-col ${denominationEnabled ? 'max-w-4xl max-h-[90vh]' : 'max-w-lg'}`}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-800">End Shift</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {activeShift?.userName} — {getDuration()} running
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : denominationEnabled ? (
            /* ── Two-column layout: details left, denomination right ── */
            <div className="flex">
              {/* Left column — stats + cash info + notes */}
              <div className="flex-1 px-6 py-5 space-y-4">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={Receipt} label="Bills" value={summary?.totalBills || 0} color="blue" />
                  <StatCard icon={IndianRupee} label="Total Sales" value={`₹${fmt(summary?.totalSales)}`} color="teal" />
                  <StatCard icon={Banknote} label="Cash Sales" value={`₹${fmt(summary?.cashSales)}`} color="emerald" />
                  <StatCard icon={Smartphone} label="Online Sales" value={`₹${fmt(summary?.onlineSales)}`} color="sky" />
                </div>

                {/* Cash Handover Summary */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                    Cash Handover
                  </p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Opening Cash</span>
                      <span className="font-semibold text-slate-800">₹{fmt(summary?.openingCash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">+ Cash Sales</span>
                      <span className="font-semibold text-slate-800">₹{fmt(summary?.cashSales)}</span>
                    </div>
                    <div className="flex justify-between border-t border-amber-200 pt-1.5">
                      <span className="font-semibold text-slate-700">Expected Cash</span>
                      <span className="font-bold text-slate-900">₹{fmt(expectedCash)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Any remarks..."
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 resize-none"
                  />
                </div>
              </div>

              {/* Right column — denomination panel */}
              <div className="w-[360px] flex-shrink-0 border-l border-slate-200 bg-slate-50/40 px-5 py-5">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
                  Cash Denomination
                </p>
                <DenominationPanel
                  counts={denomCounts}
                  onChange={setDenomCounts}
                  expectedCash={expectedCash}
                />
              </div>
            </div>
          ) : (
            /* ── Single-column layout (no denomination) ── */
            <div className="px-6 py-5 space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <StatCard icon={Receipt} label="Bills" value={summary?.totalBills || 0} color="blue" />
                <StatCard icon={IndianRupee} label="Total Sales" value={`₹${fmt(summary?.totalSales)}`} color="teal" />
                <StatCard icon={Banknote} label="Cash Sales" value={`₹${fmt(summary?.cashSales)}`} color="emerald" />
                <StatCard icon={Smartphone} label="Online Sales" value={`₹${fmt(summary?.onlineSales)}`} color="sky" />
              </div>

              {/* Cash Handover Section */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  Cash Handover
                </p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Opening Cash</span>
                    <span className="font-semibold text-slate-800">₹{fmt(summary?.openingCash)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">+ Cash Sales</span>
                    <span className="font-semibold text-slate-800">₹{fmt(summary?.cashSales)}</span>
                  </div>
                  <div className="flex justify-between border-t border-amber-200 pt-1.5">
                    <span className="font-semibold text-slate-700">Expected Cash</span>
                    <span className="font-bold text-slate-900">₹{fmt(expectedCash)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    Count Your Cash
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">₹</span>
                    <input
                      type="number"
                      value={actualCash}
                      onChange={(e) => setActualCash(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                    />
                  </div>
                </div>

                {cashDiff !== null && !isNaN(cashDiff) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Difference</span>
                    <span
                      className={`font-bold ${
                        cashDiff === 0 ? 'text-emerald-600' :
                        cashDiff < 0 ? 'text-red-600' : 'text-amber-600'
                      }`}
                    >
                      {cashDiff >= 0 ? '+' : ''}₹{fmt(cashDiff)}
                    </span>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Any remarks..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-3 flex-shrink-0 border-t border-slate-100">
          <button
            onClick={onClose}
            disabled={closing}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleClose}
            disabled={closing || loading}
            className="px-5 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-60"
          >
            {closing && <Loader2 className="w-4 h-4 animate-spin" />}
            {closing ? 'Closing...' : 'Close Shift'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    teal: 'bg-teal-50 text-teal-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    sky: 'bg-sky-50 text-sky-600',
  };
  const iconBg = colorMap[color] || colorMap.teal;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3">
      <div className={`p-1.5 rounded-lg ${iconBg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
